import { memo, useId, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InsightsMetricRow, {
  InsightsMetricUniformProvider,
} from "./InsightsMetricRow";
import { insightsUniformRowsReserveDeltaLane } from "./InsightsDeltaBadge";
import InsightsSectionHeading from "./InsightsSectionHeading";
import {
  INSIGHTS_NO_DATA_DISPLAY,
  formatInsightsMetricDisplay,
} from "../utils/insightsSetRowMath";

/**
 * Format numeric overview fields for display.
 * @param {{ id: string, value: number, muscleGroupsPair?: [number, number] }} metric
 * @returns {string}
 */
function formatOverviewValue(metric) {
  const { id: metricId, value, muscleGroupsPair } = metric;
  if (metricId === "volumeKg") {
    if (value == null || value <= 0) {
      return INSIGHTS_NO_DATA_DISPLAY;
    }
    return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  if (metricId === "muscleGroups" && muscleGroupsPair?.length === 2) {
    const [hit, total] = muscleGroupsPair;
    return `${hit}/${total}`;
  }
  if (metricId === "avgPrescribedEffort") {
    if (value == null || !Number.isFinite(value)) {
      return INSIGHTS_NO_DATA_DISPLAY;
    }
    return formatInsightsMetricDisplay(value);
  }
  return String(value);
}

/** Function to format the label for an overview metric.
 * @param {import("i18next").TFunction} t
 * @param {string} metricId
 * @param {boolean} isRir
 */
function overviewMetricLabel(t, metricId, isRir) {
  if (metricId === "avgPrescribedEffort") {
    if (isRir) {
      return t("programs.programInsights.panel.metrics.avgPrescribedRir");
    }
    return t("programs.programInsights.panel.metrics.avgPrescribedRpe");
  }
  return t(`programs.programInsights.panel.metrics.${metricId}`);
}

/** Function to get the info key for an overview metric.
 * @param {string} metricId
 * @param {boolean} isRir
 */
function overviewMetricInfoKey(metricId, isRir) {
  if (metricId === "avgPrescribedEffort") {
    if (isRir) {
      return "avgPrescribedRir";
    }
    return "avgPrescribedRpe";
  }
  return metricId;
}

/**
 * OVERVIEW block: exercises, sets, reps, muscle groups, volume.
 * Not tagged for cross-column `min-height` sync (avoids stretching the whole block to the tallest column).
 * Metric rows use `InsightsMetricUniformProvider` + `uniformLayout` (delta lane once per block).
 *
 * @param {object} props
 * @param {object} props.week — column week payload; uses `overview` for metric rows.
 * @param {string} props.insightsVolumeSuffix — volume row suffix from panel UNITS (e.g. `" kg"` / `" lb"`).
 * @param {"rpe"|"rir"} [props.insightsIntensityMeasure] — coach preference for avg prescribed effort label/value.
 */
function InsightsOverviewSection({
  week,
  insightsVolumeSuffix,
  insightsIntensityMeasure = "rpe",
}) {
  const { t } = useTranslation("athlete-portal");
  const titleId = useId();
  const metrics = week.overview;
  const volumeValueSuffix = insightsVolumeSuffix ?? " kg";
  const isRir = insightsIntensityMeasure === "rir";

  /** Aria labels for the label-side info icons (values on the right never use an info icon here). */
  const infoLabels = useMemo(
    () => ({
      exercises: t("programs.programInsights.panel.metricInfo.exercises"),
      totalSets: t("programs.programInsights.panel.metricInfo.totalSets"),
      estimatedReps: t("programs.programInsights.panel.metricInfo.estimatedReps"),
      muscleGroups: t("programs.programInsights.panel.metricInfo.muscleGroups"),
      avgPrescribedRpe: t(
        "programs.programInsights.panel.metricInfo.avgPrescribedRpe"
      ),
      avgPrescribedRir: t(
        "programs.programInsights.panel.metricInfo.avgPrescribedRir"
      ),
      volumeKg: t("programs.programInsights.panel.metricInfo.volumeKg"),
    }),
    [t]
  );

  return (
    <section className="ProgramInsights-section" aria-labelledby={titleId}>
      <InsightsSectionHeading
        titleId={titleId}
        title={t("programs.programInsights.panel.sectionOverview")}
      />
      <InsightsMetricUniformProvider
        reserveDeltaSlot={insightsUniformRowsReserveDeltaLane(metrics)}
      >
        <div className="ProgramInsights-sectionBody">
          {metrics.map((m) => (
              <InsightsMetricRow
                key={m.id}
                label={overviewMetricLabel(t, m.id, isRir)}
                valueDisplay={formatOverviewValue(m)}
                valueSuffix={
                  m.id === "volumeKg" && m.value > 0 ? volumeValueSuffix : undefined
                }
                delta={m.delta}
                infoAriaLabel={infoLabels[overviewMetricInfoKey(m.id, isRir)]}
                uniformLayout
                showNeutralDeltaBadge
              />
          ))}
        </div>
      </InsightsMetricUniformProvider>
    </section>
  );
}

InsightsOverviewSection.propTypes = {
  insightsVolumeSuffix: PropTypes.string,
  insightsIntensityMeasure: PropTypes.oneOf(["rpe", "rir"]),
  week: PropTypes.shape({
    overview: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.number,
        delta: PropTypes.object,
        muscleGroupsPair: PropTypes.arrayOf(PropTypes.number.isRequired),
      })
    ).isRequired,
  }).isRequired,
};

export default memo(InsightsOverviewSection);
